import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import { useApolloClient } from '@apollo/react-hooks';
import { fetchAuthSession, signOut as amplifySignOut, updatePassword, updateUserAttributes } from 'aws-amplify/auth';
import { cognitoCredentialsProvider } from 'aws-amplify/auth/cognito';
import * as React from 'react';
import { useAsyncFn, usePrevious } from 'react-use';
import { AppName, createLogger } from '../logging';
import { createContext } from '../react';
import session from '../session';
import { clearTokenCookie } from './init';
import { fetchAuthUser } from './user';
import { jsx as _jsx } from "react/jsx-runtime";
const logger = createLogger(AppName.Common, 'auth');
const OAUTH_PROVIDER_PREFIXES = ['Google_', 'Facebook_', 'Apple_'];
const isOAuthUser = username => OAUTH_PROVIDER_PREFIXES.some(prefix => username === null || username === void 0 ? void 0 : username.startsWith(prefix));
const [useAuth, AuthContext] = createContext();
const useAuthenticated = () => {
  const {
    authenticated
  } = useAuth();
  return authenticated;
};
const useUser = () => {
  const {
    user
  } = useAuth();
  return user;
};
const AuthConsumer = AuthContext.Consumer;
const useUserChange = (user, callback) => {
  const prevUser = usePrevious(user);
  const callbackRef = React.useRef(callback);
  React.useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  React.useEffect(() => {
    if (callbackRef.current) {
      callbackRef.current(user, prevUser);
    }
  }, [prevUser, user]);
};
const AuthProvider = ({
  children,
  onUserChange,
  FallbackComponent
}) => {
  const client = useApolloClient();
  session.config();
  const [verifyState, verify] = useAsyncFn(_asyncToGenerator(function* () {
    try {
      return yield fetchAuthUser();
    } catch {
      yield session.verify();
      return undefined;
    }
  }));
  const [changePasswordState, changePassword] = useAsyncFn(function () {
    var _ref2 = _asyncToGenerator(function* (oldPassword, newPassword) {
      if (!oldPassword || !newPassword) throw new Error();
      yield updatePassword({
        oldPassword,
        newPassword
      });
    });
    return function (_x, _x2) {
      return _ref2.apply(this, arguments);
    };
  }());
  const [updatePasswordAttributesState, updatePasswordAttributes] = useAsyncFn(_asyncToGenerator(function* () {
    yield updateUserAttributes({
      userAttributes: {
        'custom:password_update_date': new Date().toISOString(),
        'custom:force_reset_password': 'False'
      }
    });
  }));
  const [initialized, setInitialized] = React.useState(false);
  const [updatingAuthState, setUpdatingAuthState] = React.useState(false);
  const user = React.useMemo(() => verifyState.value, [verifyState]);
  useUserChange(user, onUserChange);
  React.useEffect(() => {
    verify().then(() => setInitialized(true));
  }, [verify]);
  const value = React.useMemo(() => {
    const signOut = function () {
      var _ref4 = _asyncToGenerator(function* () {
        if (user) {
          setUpdatingAuthState(true);
          if (isOAuthUser(user.username)) {
            try {
              Object.keys(window.localStorage).forEach(key => {
                if (key.startsWith('CognitoIdentityServiceProvider')) {
                  window.localStorage.removeItem(key);
                }
              });
            } catch (error) {
              logger.log('Storage cleanup error (non-critical):', error);
            }
            clearTokenCookie();
          } else {
            try {
              yield amplifySignOut({
                global: false
              });
            } catch (error) {
              logger.log('Sign out error (non-critical):', error);
            }
          }
          try {
            yield cognitoCredentialsProvider.clearCredentialsAndIdentityId();
          } catch (error) {
            logger.log('Credentials cleanup error (non-critical):', error);
          }
          yield verify();
          client.stop();
          yield client.resetStore();
          setUpdatingAuthState(false);
        }
      });
      return function signOut() {
        return _ref4.apply(this, arguments);
      };
    }();
    const userObj = user === undefined ? {
      user: undefined,
      authenticated: false
    } : {
      user,
      authenticated: true
    };
    return {
      ...userObj,
      verifyState,
      verify,
      signOut,
      changePasswordState,
      changePassword,
      updatePasswordAttributesState,
      updatePasswordAttributes,
      updatingAuthState
    };
  }, [user, verifyState, verify, changePasswordState, changePassword, updatePasswordAttributesState, updatePasswordAttributes, updatingAuthState, client]);
  if (!initialized) {
    if (FallbackComponent) {
      return _jsx(FallbackComponent, {});
    }
    return null;
  }
  return _jsx(AuthContext.Provider, {
    value: value,
    children: children
  });
};
const getToken = function () {
  var _ref5 = _asyncToGenerator(function* () {
    try {
      var _tokens$idToken;
      const {
        tokens
      } = yield fetchAuthSession();
      return tokens === null || tokens === void 0 ? void 0 : (_tokens$idToken = tokens.idToken) === null || _tokens$idToken === void 0 ? void 0 : _tokens$idToken.toString();
    } catch {
      return undefined;
    }
  });
  return function getToken() {
    return _ref5.apply(this, arguments);
  };
}();
export { AuthConsumer, AuthContext, AuthProvider, getToken, isOAuthUser, useAuth, useAuthenticated, useUser };